import { FC } from 'react';
import { Z } from '@components/v4';
import { PaulinaChatHeader } from './PaulinaChatHeader';
import { PaulinaConversation } from './PaulinaConversation';
import { PaulinaContextValue } from './PaulinaProvider';
import { usePaulinaDialogA11y } from './usePaulinaDialogA11y';

/**
 * PaulinaPanel — el chat ABIERTO en DESKTOP (Opción A). Anclado abajo-derecha,
 * z-index `Z.paulinaSheet` (95, DEBAJO del toast de pedido `Z.pedido`=99 de
 * NotificationCenterV2). Cuando hay pedido entrante el panel CEDE: se desplaza hacia
 * arriba (`bottom`) para no quedar tapado por el toast (que en desktop ocupa
 * `bottom-0 right-0`, ~400px de ancho).
 *
 * A11y: diálogo modal (`role=dialog` + `aria-modal` + focus-trap + retorno de foco al
 * activador + Escape) vía `usePaulinaDialogA11y`, modelo QuickPresentationsSheet. La
 * transición de `bottom` (animación NUEVA, la cesión ante pedidos) se desactiva con
 * `motion-reduce:transition-none` para respetar `prefers-reduced-motion`.
 *
 * Reusa el header (PaulinaChatHeader) y el render de mensajes/input
 * (PaulinaConversation) compartidos con el bottom-sheet móvil.
 *
 * GEOMETRY INVARIANT: the panel is always fully inside the viewport — its top edge never
 * goes above `PANEL_TOP_GAP_PX`, with or without an incoming order. The height cap is
 * derived from the SAME offset used to yield, so both states share one arithmetic
 * (`panelMaxHeight`). A fixed `max-h-[540px]` used to ignore the 380px shift and clipped
 * the header off-screen on every viewport shorter than 920px (768px → top = -111px).
 * `dvh` (not `vh`) tracks the real visible viewport; precedent: CommunicationsModal.tsx:17.
 */

/** Resting gap from the viewport bottom (the old Tailwind `bottom-6`). */
export const PANEL_RESTING_OFFSET_PX = 24;
/**
 * Yield gap: 20px of the toast's own margin (`mb-5` in NotificationCenterV2) plus ~360px
 * for the incoming-order card, so the panel clears the toast instead of sitting under it.
 */
export const PANEL_YIELD_OFFSET_PX = 380;
/** Breathing room kept between the panel's top edge and the top of the viewport. */
export const PANEL_TOP_GAP_PX = 24;
/** Comfortable height when the viewport has room to spare. */
export const PANEL_PREFERRED_HEIGHT_PX = 540;

// Distance from the bottom of the viewport, per yielding state.
export const panelBottomOffset = (yieldingToOrder: boolean): number =>
  yieldingToOrder ? PANEL_YIELD_OFFSET_PX : PANEL_RESTING_OFFSET_PX;

// Height cap that keeps the panel inside the viewport: whatever is left after the bottom
// offset and the top gap, never more than the comfortable height.
//
// USABLE FLOOR — deliberately NOT clamped with a `max()`: the usable floor is ~260px
// (header + one message + composer), which this formula still grants to every viewport
// >= ~664px tall while an order is on screen (664 - 380 - 24 = 260), i.e. every real
// laptop (the shortest common one is 768px). Below that the panel keeps shrinking instead
// of hitting a floor, on purpose: a small-but-whole panel beats a clipped one, and pushing
// it back down over the toast would break the operational invariant (PRJ-009 — the
// incoming-order toast always wins).
export const panelMaxHeight = (yieldingToOrder: boolean): string =>
  `min(${PANEL_PREFERRED_HEIGHT_PX}px, calc(100dvh - ${panelBottomOffset(yieldingToOrder)}px - ${PANEL_TOP_GAP_PX}px))`;

type PaulinaPanelProps = Pick<
  PaulinaContextValue,
  | 'turns'
  | 'input'
  | 'setInput'
  | 'busy'
  | 'error'
  | 'bottomRef'
  | 'goTo'
  | 'send'
  | 'doSend'
  | 'setOpen'
  | 'clearConversation'
  | 'sesionChatId'
> & { hayPedido: boolean; onDeactivate?: () => void };

export const PaulinaPanel: FC<PaulinaPanelProps> = ({
  turns,
  input,
  setInput,
  busy,
  error,
  bottomRef,
  goTo,
  send,
  doSend,
  setOpen,
  clearConversation,
  sesionChatId,
  hayPedido,
  onDeactivate,
}) => {
  const close = () => setOpen(false);
  const dialogRef = usePaulinaDialogA11y(close);

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Chat con Paulina"
      style={{
        zIndex: Z.paulinaSheet,
        // Yields to the order toast (z-99) by rising above the area it occupies.
        bottom: panelBottomOffset(hayPedido),
        height: '65dvh',
        maxHeight: panelMaxHeight(hayPedido),
      }}
      // `max-height` transitions ALONGSIDE `bottom` on purpose: animating only `bottom` would
      // snap the cap back to its taller value while the panel is still raised, flashing the very
      // overflow this fixes. Moving together, `bottom + height` interpolates monotonically, so the
      // top edge never dips below its resting value mid-animation.
      className="fixed right-6 flex w-[92vw] max-w-sm flex-col overflow-hidden rounded-xl border border-[#e2e8f0] bg-[#ffffff] text-[#253746] shadow-2xl transition-[bottom,max-height] duration-300 motion-reduce:transition-none"
    >
      <PaulinaChatHeader
        busy={busy}
        emptyConversation={turns.length === 0}
        onClear={clearConversation}
        onClose={close}
        onDeactivate={onDeactivate}
      />
      <PaulinaConversation
        turns={turns}
        input={input}
        setInput={setInput}
        busy={busy}
        error={error}
        bottomRef={bottomRef}
        goTo={goTo}
        send={send}
        doSend={doSend}
        sesionChatId={sesionChatId}
      />
    </div>
  );
};
